import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { adminAnalyticsApi, adminDashboardApi } from '../../services/api';
import { NexusCard, NexusBadge } from '../../components/nexus-ui';
import { useCountUp, useInView } from '../../hooks/useAnimations';
import { TrendingUp, DollarSign, Zap, Users, BarChart3, Activity } from 'lucide-react';

const CHART_THEME = {
  bg: '#1A1F2E',
  border: 'rgba(255,255,255,0.1)',
  grid: 'rgba(255,255,255,0.04)',
  text: '#A0AEC0',
  mint: '#1E4E8C',
  violet: '#F26B1F',
  amber: '#92580A',
  blue: '#2E70AD',
  rose: '#7A1F1F',
};

const PIE_COLORS = ['#1E4E8C', '#F26B1F', '#2E70AD', '#92580A', '#7A1F1F', '#B4BCC7'];
const TIME_RANGES = ['7d', '30d', '90d'];

function StatCard({ label, value, icon: Icon, color, prefix = '', suffix = '' }) {
  const [ref, inView] = useInView();
  const animated = useCountUp(value, 1200, inView);

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <NexusCard variant="glass" className="p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon size={20} style={{ color }} />
          </div>
          <div>
            <p className="text-xs text-nexus-text-tertiary uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-heading font-bold text-nexus-text-primary">
              {prefix}{typeof value === 'number' ? animated.toLocaleString('en-IN') : value}{suffix}
            </p>
          </div>
        </div>
      </NexusCard>
    </motion.div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-nexus-bg-tertiary border border-nexus-border-emphasized rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-nexus-text-tertiary mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-medium" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const AdminAnalytics = () => {
  const [range, setRange] = useState('30d');

  const { data: stats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => adminDashboardApi.getStats().then(r => r.data),
  });

  const { data: timeseries = [] } = useQuery({
    queryKey: ['adminTimeseries', range],
    queryFn: () => adminAnalyticsApi.getTimeseries(range).then(r => r.data),
  });

  const { data: topTenants = [] } = useQuery({
    queryKey: ['adminTopTenants'],
    queryFn: () => adminAnalyticsApi.getTopTenants(8).then(r => r.data),
  });

  const { data: modelDist = [] } = useQuery({
    queryKey: ['adminModelDist'],
    queryFn: () => adminAnalyticsApi.getModelDistribution().then(r => r.data),
  });

  const totalTokens = timeseries.reduce((s, d) => s + d.tokens, 0);
  const totalAPICalls = timeseries.reduce((s, d) => s + d.api_calls, 0);
  const totalRevenue = timeseries.reduce((s, d) => s + (d.revenue || 0), 0);
  const totalConvos = timeseries.reduce((s, d) => s + d.conversations, 0);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">Analytics</h2>
          <p className="text-nexus-text-secondary text-sm mt-1">Platform usage and revenue metrics</p>
        </div>
        <div className="flex gap-1 p-1 bg-nexus-bg-tertiary/50 rounded-lg border border-white/5">
          {TIME_RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              data-testid={`range-${r}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                range === r ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint' : 'text-nexus-text-tertiary hover:text-nexus-text-secondary'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={totalRevenue || stats?.revenue_inr || 0} icon={DollarSign} color={CHART_THEME.mint} prefix="Rs. " />
        <StatCard label="API Calls" value={totalAPICalls} icon={Zap} color={CHART_THEME.violet} />
        <StatCard label="Tokens Used" value={totalTokens || stats?.total_tokens_used || 0} icon={BarChart3} color={CHART_THEME.amber} />
        <StatCard label="Active Tenants" value={stats?.active_tenants || 0} icon={Users} color={CHART_THEME.blue} />
      </div>

      {/* Token Usage + Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4 flex items-center gap-2">
                <Activity size={16} className="text-nexus-accent-mint" /> Token Consumption
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <defs>
                    <linearGradient id="inputGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_THEME.mint} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={CHART_THEME.mint} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="outputGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_THEME.violet} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={CHART_THEME.violet} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area dataKey="input_tokens" name="Input" stroke={CHART_THEME.mint} fill="url(#inputGrad)" strokeWidth={2} />
                  <Area dataKey="output_tokens" name="Output" stroke={CHART_THEME.violet} fill="url(#outputGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4 flex items-center gap-2">
                <DollarSign size={16} className="text-nexus-accent-mint" /> Revenue (Rs.)
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART_THEME.amber} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={CHART_THEME.amber} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area dataKey="revenue" name="Revenue (Rs.)" stroke={CHART_THEME.amber} fill="url(#revenueGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </motion.div>
      </div>

      {/* API Calls + Conversations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4">API Calls</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="api_calls" name="API Calls" fill={CHART_THEME.violet} radius={[4, 4, 0, 0]} animationDuration={1200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4">Conversations</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_THEME.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: CHART_THEME.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="conversations" name="Conversations" fill={CHART_THEME.blue} radius={[4, 4, 0, 0]} animationDuration={1200} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </motion.div>
      </div>

      {/* Model Distribution + Top Tenants */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4">Model Distribution</h3>
              {modelDist.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={modelDist}
                      dataKey="calls"
                      nameKey="model"
                      cx="50%" cy="50%"
                      outerRadius={90}
                      innerRadius={50}
                      animationDuration={1200}
                      label={({ model, percent }) => `${model.split('-')[0]} ${(percent * 100).toFixed(0)}%`}
                    >
                      {modelDist.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-nexus-text-tertiary text-sm">No model usage data yet</div>
              )}
            </div>
          </NexusCard>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <h3 className="font-heading text-base font-semibold text-nexus-text-primary mb-4">Top Tenants by Usage</h3>
              {topTenants.length > 0 ? (
                <div className="space-y-3 max-h-[260px] overflow-y-auto">
                  {topTenants.map((t, i) => (
                    <div key={t.tenant_id} className="flex items-center justify-between p-2 rounded-lg bg-nexus-bg-tertiary/30" data-testid={`top-tenant-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-nexus-text-tertiary w-5">#{i + 1}</span>
                        <div>
                          <p className="text-sm font-medium text-nexus-text-primary">{t.name}</p>
                          <p className="text-xs text-nexus-text-tertiary">{t.slug}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-nexus-accent-mint">{t.total_tokens.toLocaleString()} tokens</p>
                        <p className="text-xs text-nexus-text-tertiary">{t.total_calls} calls</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[260px] flex items-center justify-center text-nexus-text-tertiary text-sm">No tenant usage data yet</div>
              )}
            </div>
          </NexusCard>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
